import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Alert,
  List,
  Space,
  Tag
} from 'antd';
import { 
  FullscreenOutlined, 
  ReloadOutlined, 
  LoginOutlined, 
  LogoutOutlined
} from '@ant-design/icons';
import { useAuth } from './AuthContext';
import crossWindowSync from '../../../utils/crossWindowSync';
import { logger } from '../../../utils/logger';

const CrossWindowTest = () => {
  const { isAuthenticated, user, login, logout } = useAuth();
  const [activeTabs, setActiveTabs] = useState(0);
  const [syncEvents, setSyncEvents] = useState([]);

  useEffect(() => {
    // 監聽同步事件
    const handleSyncEvent = (data) => {
      setSyncEvents(prev => [
        {
          timestamp: new Date().toLocaleTimeString(),
          type: data.type,
          payload: data.payload,
          tabId: data.tabId
        },
        ...prev.slice(0, 9) // 只保留最近10個事件
      ]);
    };

    crossWindowSync.on('auth_status', handleSyncEvent);

    // 定期檢查活躍標籤數量
    const interval = setInterval(() => {
      setActiveTabs(crossWindowSync.getActiveTabCount());
    }, 2000);

    return () => {
      crossWindowSync.off('auth_status', handleSyncEvent);
      clearInterval(interval);
    };
  }, []);

  const handleOpenNewWindow = () => {
    window.open(window.location.href, '_blank');
  };

  const handleTestLogin = async () => {
    try {
      // 這裡使用測試帳號，實際使用時應該讓用戶輸入
      await login('test@example.com', 'password123');
    } catch (error) {
      logger.error('測試登入失敗:', error);
    }
  };

  const handleTestLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('測試登出失敗:', error);
    }
  };

  const getCurrentAuthStatus = () => {
    return crossWindowSync.getCurrentAuthStatus();
  };

  const getLoginTimeInfo = () => {
    const loginTime = localStorage.getItem('loginTime');
    if (!loginTime) return null;

    const loginTimestamp = parseInt(loginTime);
    const tenDaysInMs = 10 * 24 * 60 * 60 * 1000; // 10天
    const currentTime = Date.now();
    const timeElapsed = currentTime - loginTimestamp;
    const timeRemaining = tenDaysInMs - timeElapsed;

    return {
      loginTime: new Date(loginTimestamp).toLocaleString(),
      timeElapsed: Math.floor(timeElapsed / 1000 / 60), // 分鐘
      timeRemaining: Math.floor(timeRemaining / 1000 / 60), // 分鐘
      isExpired: timeRemaining <= 0
    };
  };

  const formatTime = (minutes) => {
    if (minutes < 0) return '已過期';
    if (minutes >= 1440) { // 24小時
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return `${days}天${hours}小時`;
    } else if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}小時${mins}分鐘`;
    }
    return `${minutes}分鐘`;
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
      <Typography.Title level={2} style={{ marginBottom: 16 }}>
        跨視窗認證同步測試
      </Typography.Title>
      
      <Alert 
        message="信息" 
        description="此頁面用於測試跨視窗認證同步功能。打開多個視窗，在其中一個視窗登入或登出，其他視窗會自動同步認證狀態。"
        type="info" 
        style={{ marginBottom: 24 }}
        showIcon
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* 當前狀態 */}
        <Card>
          <Typography.Title level={4} style={{ marginBottom: 16 }}>
            當前認證狀態
          </Typography.Title>
          <div style={{ marginBottom: 16 }}>
            <Tag 
              color={isAuthenticated ? 'green' : 'default'}
              style={{ marginRight: 8 }}
            >
              {isAuthenticated ? '已登入' : '未登入'}
            </Tag>
            <Tag color="blue">
              活躍視窗: {activeTabs}
            </Tag>
          </div>
          {user && (
            <Typography.Text type="secondary">
              用戶: {user.name || user.email}
            </Typography.Text>
          )}
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            標籤ID: {crossWindowSync.tabId}
          </Typography.Text>
        </Card>

        {/* 操作按鈕 */}
        <Card>
          <Typography.Title level={4} style={{ marginBottom: 16 }}>
            測試操作
          </Typography.Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              type="default"
              icon={<FullscreenOutlined />}
              onClick={handleOpenNewWindow}
              block
            >
              開啟新視窗
            </Button>
            <Button
              type="primary"
              icon={<LoginOutlined />}
              onClick={handleTestLogin}
              disabled={isAuthenticated}
              block
            >
              測試登入
            </Button>
            <Button
              type="default"
              icon={<LogoutOutlined />}
              onClick={handleTestLogout}
              disabled={!isAuthenticated}
              block
            >
              測試登出
            </Button>
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
              block
            >
              刷新頁面
            </Button>
          </Space>
        </Card>
      </div>

      {/* 本地存儲狀態 */}
      <Card style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          本地存儲狀態
        </Typography.Title>
        <div style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4, marginBottom: 16 }}>
          <Typography.Text style={{ margin: 0, fontSize: '14px', fontFamily: 'monospace' }}>
            {JSON.stringify(getCurrentAuthStatus(), null, 2)}
          </Typography.Text>
        </div>
        
        {/* 登入時間信息 */}
        {isAuthenticated && (
          <div>
            <Typography.Title level={5} style={{ marginBottom: 16 }}>
              登入時間信息
            </Typography.Title>
            {(() => {
              const loginInfo = getLoginTimeInfo();
              if (!loginInfo) return <Typography.Text type="secondary">無登入時間記錄</Typography.Text>;
              
              return (
                <div style={{ backgroundColor: '#f0f8ff', padding: 16, borderRadius: 4 }}>
                  <Typography.Text style={{ display: 'block', marginBottom: 8 }}>
                    <strong>登入時間：</strong> {loginInfo.loginTime}
                  </Typography.Text>
                  <Typography.Text style={{ display: 'block', marginBottom: 8 }}>
                    <strong>已登入：</strong> {formatTime(loginInfo.timeElapsed)}
                  </Typography.Text>
                  <Typography.Text style={{ display: 'block', marginBottom: 8 }}>
                    <strong>剩餘時間：</strong> 
                    <Tag 
                      color={loginInfo.isExpired ? 'red' : loginInfo.timeRemaining < 60 ? 'orange' : 'green'}
                      style={{ marginLeft: 8 }}
                    >
                      {formatTime(loginInfo.timeRemaining)}
                    </Tag>
                  </Typography.Text>
                  {loginInfo.timeRemaining < 60 && !loginInfo.isExpired && (
                    <Alert 
                      message="警告" 
                      description={`您的登入會話即將在 ${formatTime(loginInfo.timeRemaining)} 後過期`}
                      type="warning" 
                      style={{ marginTop: 8 }}
                      showIcon
                    />
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </Card>

      {/* 同步事件記錄 */}
      <Card>
        <Typography.Title level={4} style={{ marginBottom: 16 }}>
          同步事件記錄
        </Typography.Title>
        {syncEvents.length === 0 ? (
          <Typography.Text type="secondary">
            尚無同步事件
          </Typography.Text>
        ) : (
          <List
            dataSource={syncEvents}
            renderItem={(event, index) => (
              <List.Item key={index}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <Tag color="blue">
                    {event.type}
                  </Tag>
                  <Typography.Text type="secondary">
                    {event.timestamp}
                  </Typography.Text>
                </div>
                <Typography.Text style={{ fontSize: '12px', fontFamily: 'monospace', display: 'block', marginTop: 8 }}>
                  {JSON.stringify(event.payload, null, 2)}
                </Typography.Text>
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};

export default CrossWindowTest; 